import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SafeImage from "@/components/base/SafeImage";
import { getSpotsByDistrict } from "@/services/touristSpotService";
import { getSpotFallbackImage } from "@/utils/fallbackImage";
import type { TouristSpot } from "@/services/touristSpotService";

interface TouristSpotsProps {
  districtSlug: string;
  districtName: string;
}

export default function TouristSpots({ districtSlug, districtName }: TouristSpotsProps) {
  const [spots, setSpots] = useState<TouristSpot[]>([]);

  useEffect(() => {
    let active = true;
    getSpotsByDistrict(districtSlug)
      .then((list) => {
        if (active) setSpots(list);
      })
      .catch(() => {
        if (active) setSpots([]);
      });
    return () => {
      active = false;
    };
  }, [districtSlug]);

  if (spots.length === 0) return null;

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading text-xs font-bold uppercase tracking-wider text-foreground-900">
          <span className="flex h-4 w-4 items-center justify-center text-accent-600">
            <i className="ri-camera-lens-line text-base" />
          </span>
          Major Spots in {districtName}
        </h3>
        <span className="rounded-full bg-accent-100 px-2.5 py-1 text-[11px] font-bold text-accent-900">
          {spots.length}
        </span>
      </div>
      <p className="mt-2 text-xs leading-relaxed text-foreground-500">
        Popular tourist places to explore around the district. Tap any spot to see photos, timings
        and nearby businesses.
      </p>

      <ul className="mt-4 space-y-2">
        {spots.map((spot) => (
          <li key={spot.id}>
            <Link
              to={`/spot/${spot.slug}`}
              className="group flex items-center gap-3 rounded-md border border-background-200 bg-background-100/60 p-2 transition-colors duration-200 hover:border-accent-300 hover:bg-accent-100/70"
            >
              <span className="h-14 w-16 shrink-0 overflow-hidden rounded-md">
                <SafeImage
                  src={spot.image}
                  fallback={getSpotFallbackImage(spot)}
                  alt={`${spot.name} - ${spot.category} in ${districtName}`}
                  title={`${spot.name} - ${spot.category}`}
                  className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold text-foreground-900 transition-colors duration-200 group-hover:text-accent-800">
                  {spot.name}
                </span>
                <span className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span className="rounded-full bg-background-200 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-foreground-600">
                    {spot.category}
                  </span>
                  <span className="truncate text-[11px] text-foreground-500">{spot.idealDuration}</span>
                </span>
              </span>
              <span className="flex h-4 w-4 shrink-0 items-center justify-center text-foreground-400 transition-colors duration-200 group-hover:text-accent-700">
                <i className="ri-arrow-right-s-line text-base" />
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}